import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  inject,
  Input,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import {
  chatFileError,
  ChatComposerSubmit,
  CHAT_ACCEPT_TYPES,
  CHAT_EMOJI_PALETTE,
  CHAT_MAX_BODY_LENGTH,
  CHAT_MAX_FILES_PER_MESSAGE,
  formatFileSize,
} from '../../models/chat.models';
import { durationLabel } from '../../utils/chat-date.util';

/** A file waiting to be sent, plus the reason it cannot be — see {@link chatFileError}. */
export interface StagedFile {
  file: File;
  error: string | null;
}

/**
 * The message box: auto-growing textarea, Enter to send, Shift+Enter for a newline, plus
 * attachments, emoji and voice notes.
 *
 * Deliberately dumb — it owns the draft and nothing else. Validation, upload and send all
 * happen in {@link ChatStore}, so the same composer works inside the full page and the floating
 * widget without either of them passing it services.
 */
@Component({
  selector: 'app-chat-composer',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './chat-composer.component.html',
  styleUrls: ['./chat-composer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatComposerComponent implements AfterViewInit, OnDestroy {
  private readonly toastr = inject(ToastrService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly host: ElementRef<HTMLElement> = inject(ElementRef);

  @ViewChild('input') private input?: ElementRef<HTMLTextAreaElement>;
  @ViewChild('fileInput') private fileInput?: ElementRef<HTMLInputElement>;

  @Input() disabled = false;
  @Input() placeholder = 'Type a message…';

  /** Why the box is shut, when it is — rate limited, or the other party is deactivated. */
  @Input() notice = '';

  /** Widget variant: drops the keyboard-shortcut hint, which is desktop advice in a 380px panel. */
  @Input() compact = false;

  @Output() submitted = new EventEmitter<ChatComposerSubmit>();
  /** Fires on every keystroke so the store can throttle a typing frame onto the socket. */
  @Output() typing = new EventEmitter<void>();

  readonly emojis = CHAT_EMOJI_PALETTE;
  readonly maxLength = CHAT_MAX_BODY_LENGTH;
  readonly acceptTypes = CHAT_ACCEPT_TYPES;
  readonly maxFiles = CHAT_MAX_FILES_PER_MESSAGE;
  readonly formatFileSize = formatFileSize;
  readonly durationLabel = durationLabel;

  text = '';
  files: StagedFile[] = [];
  emojiOpen = false;

  /** Set when files were refused outright — over the cap — rather than staged with an error. */
  attachmentNotice = '';

  recording = false;
  recordingMs = 0;

  /** Roughly six lines; past that the box scrolls instead of eating the transcript. */
  private static readonly MAX_HEIGHT_PX = 108;

  private mediaRecorder: MediaRecorder | null = null;
  private recordedChunks: Blob[] = [];
  private recordingTimer: any;
  private recordingStartedAt = 0;
  private mediaStream: MediaStream | null = null;

  ngAfterViewInit(): void {
    // Claim the height inline immediately; otherwise the global `textarea` rule renders a
    // 150px-tall box until the first keystroke.
    this.autoGrow();
  }

  /** Only files that would actually be accepted — the rest are on screen with a reason. */
  get sendableFiles(): File[] {
    return this.files.filter(staged => !staged.error).map(staged => staged.file);
  }

  get canSend(): boolean {
    if (this.disabled) {
      return false;
    }
    // A message made up entirely of rejected files has nothing to send.
    return this.text.trim().length > 0 || this.sendableFiles.length > 0;
  }

  get remaining(): number {
    return this.maxLength - this.text.length;
  }

  /** Only surfaced when the user is genuinely close to the cap. */
  get showRemaining(): boolean {
    return this.remaining <= 200;
  }

  // ---- Typing ----------------------------------------------------------------

  onKeydown(event: KeyboardEvent): void {
    // Enter sends, Shift+Enter inserts a newline — the Slack/Teams convention the brief asks for.
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.submit();
    }
  }

  onInput(): void {
    this.autoGrow();
    this.typing.emit();
  }

  submit(): void {
    if (!this.canSend) {
      return;
    }
    this.submitted.emit({ text: this.text.trim(), files: this.sendableFiles });
    this.text = '';
    this.files = [];
    this.attachmentNotice = '';
    this.emojiOpen = false;
    this.resetHeight();
    this.focus();
  }

  focus(): void {
    setTimeout(() => this.input?.nativeElement?.focus());
  }

  /**
   * Size the box to its content, between one line and {@link MAX_HEIGHT_PX}.
   *
   * The inline height this writes is also what keeps the app-wide `textarea { height: 150px }`
   * rule from applying — see the note in this component's SCSS.
   */
  private autoGrow(): void {
    const el = this.input?.nativeElement;
    if (!el) {
      return;
    }
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, ChatComposerComponent.MAX_HEIGHT_PX)}px`;
  }

  /** Back to a single line after sending. */
  private resetHeight(): void {
    const el = this.input?.nativeElement;
    if (el) {
      el.style.height = '';
      // Re-measure rather than trusting the CSS floor, so an empty box is exactly one line.
      this.autoGrow();
    }
  }

  // ---- Attachments -----------------------------------------------------------

  openFilePicker(): void {
    if (this.disabled) {
      return;
    }
    this.fileInput?.nativeElement?.click();
  }

  onFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.addFiles(Array.from(input.files || []));
    // Reset so re-picking the same file still fires a change event.
    input.value = '';
  }

  /** Screenshots pasted straight into the box, which is how most people share one. */
  onPaste(event: ClipboardEvent): void {
    if (this.disabled) {
      return;
    }
    const pasted = Array.from(event.clipboardData?.items || [])
      .filter(item => item.kind === 'file')
      .map(item => item.getAsFile())
      .filter((file): file is File => !!file);

    if (pasted.length) {
      event.preventDefault();
      this.addFiles(pasted);
    }
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.dragging = false;
    if (this.disabled) {
      return;
    }
    this.addFiles(Array.from(event.dataTransfer?.files || []));
  }

  dragging = false;

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    // No drop highlight on a composer that will refuse the drop.
    this.dragging = !this.disabled;
  }

  onDragLeave(): void {
    this.dragging = false;
  }

  removeFile(index: number): void {
    this.files = this.files.filter((_, i) => i !== index);
    if (this.files.length < this.maxFiles) {
      this.attachmentNotice = '';
    }
  }

  /**
   * Stage files, capped and validated.
   *
   * Two different refusals, deliberately shown differently: anything over the per-message cap is
   * never staged at all (there is no row to put a message on), while a file that is too large or
   * of a blocked type *is* staged, with the reason on its own row — so the user can see which of
   * five files is the problem and drop just that one.
   */
  private addFiles(incoming: File[]): void {
    if (this.disabled || !incoming.length) {
      return;
    }

    const room = Math.max(0, this.maxFiles - this.files.length);
    const accepted = incoming.slice(0, room);
    const rejected = incoming.length - accepted.length;

    this.attachmentNotice = rejected > 0 ? `You can attach up to ${this.maxFiles} files per message.` : '';
    this.files = [...this.files, ...accepted.map(file => ({ file, error: chatFileError(file) }))];
    this.cdr.markForCheck();
  }

  isImage(file: File): boolean {
    return (file.type || '').startsWith('image/');
  }

  // ---- Emoji -----------------------------------------------------------------

  toggleEmoji(): void {
    this.emojiOpen = !this.emojiOpen;
  }

  /** Insert at the caret rather than appending, so emoji can land mid-sentence. */
  insertEmoji(emoji: string): void {
    const el = this.input?.nativeElement;
    if (!el) {
      this.text += emoji;
      return;
    }
    const start = el.selectionStart ?? this.text.length;
    const end = el.selectionEnd ?? this.text.length;
    this.text = this.text.slice(0, start) + emoji + this.text.slice(end);
    this.emojiOpen = false;
    setTimeout(() => {
      el.focus();
      const caret = start + emoji.length;
      el.setSelectionRange(caret, caret);
      this.autoGrow();
    });
  }

  // ---- Voice notes -----------------------------------------------------------

  get voiceSupported(): boolean {
    return typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia && typeof MediaRecorder !== 'undefined';
  }

  async toggleRecording(): Promise<void> {
    if (this.recording) {
      this.stopRecording(true);
      return;
    }
    if (!this.voiceSupported) {
      this.toastr.info('Voice messages are not supported in this browser.');
      return;
    }

    try {
      this.mediaStream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      this.toastr.error('Microphone access was blocked.');
      return;
    }

    this.recordedChunks = [];
    // Let the browser pick its own container: Chrome/Firefox give webm, Safari gives mp4.
    this.mediaRecorder = new MediaRecorder(this.mediaStream);
    this.mediaRecorder.ondataavailable = event => {
      if (event.data.size) {
        this.recordedChunks.push(event.data);
      }
    };
    this.mediaRecorder.start();

    this.recording = true;
    this.recordingStartedAt = Date.now();
    this.recordingMs = 0;
    this.recordingTimer = setInterval(() => {
      this.recordingMs = Date.now() - this.recordingStartedAt;
      // Match the 5-minute cap in the backend spec rather than letting a forgotten
      // recording run until the upload is rejected.
      if (this.recordingMs > 5 * 60 * 1000) {
        this.stopRecording(true);
      }
      this.cdr.markForCheck();
    }, 200);
  }

  cancelRecording(): void {
    this.stopRecording(false);
  }

  private stopRecording(send: boolean): void {
    const recorder = this.mediaRecorder;
    clearInterval(this.recordingTimer);
    this.recording = false;

    if (!recorder) {
      return;
    }

    const durationMs = Date.now() - this.recordingStartedAt;
    recorder.onstop = () => {
      this.releaseMicrophone();
      if (!send || !this.recordedChunks.length) {
        this.recordedChunks = [];
        this.cdr.markForCheck();
        return;
      }
      const type = recorder.mimeType || 'audio/webm';
      const blob = new Blob(this.recordedChunks, { type });
      const extension = type.includes('mp4') ? 'm4a' : 'webm';
      const file = new File([blob], `voice-message-${Date.now()}.${extension}`, { type });
      this.recordedChunks = [];
      // A voice note is sent on its own — mixing it with typed text muddles both.
      this.submitted.emit({ text: '', files: [file], voiceDurationMs: durationMs });
      this.cdr.markForCheck();
    };

    try {
      recorder.stop();
    } catch {
      this.releaseMicrophone();
    }
    this.mediaRecorder = null;
  }

  private releaseMicrophone(): void {
    this.mediaStream?.getTracks().forEach(track => track.stop());
    this.mediaStream = null;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.emojiOpen && !this.host.nativeElement.contains(event.target as Node)) {
      this.emojiOpen = false;
      this.cdr.markForCheck();
    }
  }

  ngOnDestroy(): void {
    clearInterval(this.recordingTimer);
    this.releaseMicrophone();
  }
}
